import React, { use, useState } from "react";
import { X } from "lucide-react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Input from "../../pagesSpecific/Login/Input";
import Button from "../../Reusable/Button";
import ClickableLinkEmbeddedText from "../../Reusable/ClickableLinkEmbeddedText";
import useForm from "../../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../../constants/fieldTypes";
import { useThunk } from "../../../hooks/useThunk";
import { resetPassword } from "../../../store/thunks/authThunks";
import showToast from "../../../utils/toastNotifications";
import { useSearchParams, useNavigate } from "react-router-dom";

const ResetPasswordScreen = ({ isOpen = true, onClose, variant, handlers }) => {
  const [params] = useSearchParams();
  const resetToken = params.get("token");
  const userId = params.get("id"); 
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [doResetPassword, isResetPasswordLoading] = useThunk(resetPassword);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      password: "",
      confirmPassword: "",
    },
    {
      password: true,
      confirmPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ password: true, confirmPassword: true });

    if (!validateForm()) return;

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      showToast("error", "Passwords do not match");
      return;
    }

    const submitData = {
      userId,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      resetToken,
    };

    try {
      const result = await doResetPassword(submitData);

      if (result?.success) {
        showToast("success", "Password reset successful");
        // after successful, open sign in modal
        handlers?.switchToSignIn?.();
      } else {
        showToast(
          "error",
          result?.error?.message || "Failed to reset password"
        );
      }
    } catch (err) {
      console.error("Reset password error:", err);
      showToast("error", "Something went wrong while resetting password");
    }
  };


  const isButtonDisabled =
  !formData.password ||
  !formData.confirmPassword ||
  isResetPasswordLoading || errors.password ||
  errors.confirmPassword;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-[566px] h-[600px] bg-[var(--color-background-01)] rounded-[16px] p-[48px]"
        style={{
          opacity: 0.9,
          boxShadow: "var(--form-box-shadow)",
          backdropFilter: "var(--form-backdrop)",
          border: "1px solid var(--color-stroke-01)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "32px",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-[var(--color-content-02)] hover:text-[var(--color-content)] transition-colors"
        >
          <X size={20} />
        </button>

        {/* Title */}
        <div className="text-center">
          <h1
            className="text-3xl sm:text-[36px] font-bold font-inter"
            style={{
              background: "var(--signin)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Reset Password
          </h1>
        </div>

        {/* Form */}
        <div className="flex flex-col pt-2 gap-6">
          <div className="min-h-[90px]">
            <Input
              label="New Password"
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter Your New Password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={() => handleBlur("password", formData.password)}
              onFocus={() => clearError("password")}
              error={touched.password && errors.password}
              isRequired
              inputStyle="bg-transparent text-[var(--color-content-03)] rounded-lg h-12 px-4"
              labelStyle="font-inter font-medium text-sm text-[var(--color-label)]"
              style={{
                width: "100%",
                maxWidth: "470px",
                height: "41px",
                transform: "rotate(0deg)",
                opacity: 1,
                borderWidth: "0.6px",
              }}
              icon={
                showPassword ? (
                  <AiFillEye
                    className="w-5 h-5 cursor-pointer text-[var(--color-content-02)]"
                    onClick={() => setShowPassword(false)}
                  />
                ) : (
                  <AiFillEyeInvisible
                    className="w-5 h-5 cursor-pointer text-[var(--color-content-02)]"
                    onClick={() => setShowPassword(true)}
                  />
                )
              }
              iconPosition="right"
            />
          </div>

          <div className="min-h-[90px]">
            <Input
              label="Confirm Password"
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Your New Password"
              value={formData.confirmPassword}
              onChange={(e) =>
                handleInputChange("confirmPassword", e.target.value)
              }
              onBlur={() =>
                handleBlur("confirmPassword", formData.confirmPassword)
              }
              onFocus={() => clearError("confirmPassword")}
              error={touched.confirmPassword && errors.confirmPassword}
              isRequired
              inputStyle="bg-transparent text-[var(--color-content-03)] rounded-lg h-12 px-4"
              labelStyle="font-inter font-medium text-sm text-[var(--color-label)]"
              style={{
                width: "100%",
                maxWidth: "470px",
                height: "41px",
                transform: "rotate(0deg)",
                opacity: 1,
                borderWidth: "0.6px",
              }}
              icon={
                showConfirmPassword ? (
                  <AiFillEye
                    className="w-5 h-5 cursor-pointer text-[var(--color-content-02)]"
                    onClick={() => setShowConfirmPassword(false)}
                  />
                ) : (
                  <AiFillEyeInvisible
                    className="w-5 h-5 cursor-pointer text-[var(--color-content-02)]"
                    onClick={() => setShowConfirmPassword(true)}
                  />
                )
              }
              iconPosition="right"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={isButtonDisabled}
            variant="login"
            loading={isResetPasswordLoading}
            style={{
              maxWidth: "470px",
              width: "100%",
              height: "51px",
              transform: "rotate(0deg)",
              borderRadius: "6px",
              paddingLeft: "5%",
              paddingRight: "5%",
              boxSizing: "border-box",
            }}
            className={`font-medium mt-6 sm:mt-10 lg:mt-[80px] ${isButtonDisabled ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}
          >
            {isResetPasswordLoading
              ? "Resetting Password..."
              : "Reset Password"}
          </Button>

          <div className="flex justify-center w-full">
            <ClickableLinkEmbeddedText
              fullText="Remember Password? Login"
              linkedText="Login"
              onClick= {handlers?.switchToSignIn}
              textColor="var(--color-content-01)"
              linkColor="var(--color-primary)"
              className="text-center"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "100%",
                display: "inline-flex",
                justifyContent: "center",
                textAlign: "center",
              }}
              linkStyle={{
                fontWeight: 600,
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
                lineHeight: "100%",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
